import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { AuthModal } from "./components/AuthModal";
import { Home } from "./pages/Home";
import { About } from "./pages/About";
import { RestaurantPartner } from "./pages/RestaurantPartner";
import { AddMenu } from "./pages/AddMenu";
import { RestaurantSuccess } from "./pages/RestaurantSuccess";
import { RestaurantLogin } from "./pages/RestaurantLogin";
import { RestaurantDashboard } from "./pages/RestaurantDashboard";
import { DeliveryPartner } from "./pages/DeliveryPartner";
import { DeliveryKYC } from "./pages/DeliveryKYC";
import { DeliverySuccess } from "./pages/DeliverySuccess";
import { DeliveryDashboard } from "./pages/DeliveryDashboard";
import { Support } from "./pages/Support";
import { Privacy } from "./pages/Privacy";
import { Terms } from "./pages/Terms";
import { Security } from "./pages/Security";
import { AdminLogin } from "./pages/admin/AdminLogin";
import { AdminDashboard } from "./pages/admin/AdminDashboard";
import { EmployeeAdminLogin } from "./pages/employee/EmployeeAdminLogin";
import { EmployeeAdminDashboard } from "./pages/employee/EmployeeAdminDashboard";
import { EmployeeLogin } from "./pages/employee/EmployeeLogin";
import { EmployeePortalDashboard } from "./pages/employee/EmployeePortalDashboard";
import { OrderTracking } from "./pages/OrderTracking";

// Layout wrapper to conditionally show Navbar/Footer
function AppLayout({ children, showAuth, setShowAuth, user, onLogout, onAuthSuccess }) {
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin');
  const isEmployeePage = location.pathname.startsWith('/employee');

  return (
    <>
      {!isAdminPage && !isEmployeePage && (
        <Navbar 
          onAuthClick={() => setShowAuth(true)} 
          user={user} 
          onLogout={onLogout} 
        />
      )}
      
      <main className="flex-1">
        {children}
      </main>

      {!isAdminPage && !isEmployeePage && <Footer />}

      <AuthModal
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        onSuccess={onAuthSuccess}
      />

      <Toaster 
        position="top-right" 
        richColors 
        toastOptions={{
          style: {
            fontFamily: 'Plus Jakarta Sans, sans-serif',
          },
        }}
      />
    </>
  );
}

function App() {
  const [showAuth, setShowAuth] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check for existing user session
    const storedUser = localStorage.getItem('fudco_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        localStorage.removeItem('fudco_user');
        localStorage.removeItem('fudco_token');
      }
    }
  }, []);

  const handleAuthSuccess = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('fudco_user');
    localStorage.removeItem('fudco_token');
    setUser(null);
  };

  return (
    <div className="App min-h-screen flex flex-col">
      <BrowserRouter>
        <AppLayout 
          showAuth={showAuth} 
          setShowAuth={setShowAuth} 
          user={user} 
          onLogout={handleLogout}
          onAuthSuccess={handleAuthSuccess}
        >
          <Routes>
            <Route path="/" element={<Home onAuthClick={() => setShowAuth(true)} />} />
            <Route path="/about" element={<About />} />
            <Route path="/restaurant-partner" element={<RestaurantPartner />} />
            <Route path="/restaurant-partner/add-menu" element={<AddMenu />} />
            <Route path="/restaurant-partner/success" element={<RestaurantSuccess />} />
            <Route path="/restaurant-login" element={<RestaurantLogin />} />
            <Route path="/restaurant-dashboard" element={<RestaurantDashboard />} />
            <Route path="/delivery-partner" element={<DeliveryPartner />} />
            <Route path="/delivery-partner/kyc" element={<DeliveryKYC />} />
            <Route path="/delivery-partner/success" element={<DeliverySuccess />} />
            <Route path="/delivery-partner/dashboard" element={<DeliveryDashboard />} />
            <Route path="/support" element={<Support />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/security" element={<Security />} />
            {/* Admin Routes */}
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            {/* Employee Admin Routes */}
            <Route path="/employee-admin" element={<EmployeeAdminLogin />} />
            <Route path="/employee-admin/dashboard" element={<EmployeeAdminDashboard />} />
            {/* Employee Portal Routes */}
            <Route path="/employee-portal" element={<EmployeeLogin />} />
            <Route path="/employee-portal/dashboard" element={<EmployeePortalDashboard />} />
            {/* Customer Order Tracking */}
            <Route path="/track" element={<OrderTracking />} />
            <Route path="/track/:orderId" element={<OrderTracking />} />
          </Routes>
        </AppLayout>
      </BrowserRouter>
    </div>
  );
}

export default App;
